import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { tenantApi } from '../services/tenantApi';
import { Employee } from '../state/tenantAuth';

interface EmployeesFilters {
  page?: number;
  per_page?: number;
  search?: string;
  location_id?: string;
  is_active?: boolean;
}

function extractErrorMessage(error: unknown, fallback: string): string {
  const err = error as { response?: { data?: { message?: string } } };
  return err?.response?.data?.message || fallback;
}

async function refreshEmployeeQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  employeeId?: string,
) {
  await queryClient.invalidateQueries({ queryKey: ['employees'] });
  await queryClient.refetchQueries({ queryKey: ['employees'], type: 'active' });
  if (employeeId) {
    await queryClient.invalidateQueries({ queryKey: ['employee', employeeId] });
  }
}

export function useEmployees(filters: EmployeesFilters) {
  return useQuery({
    queryKey: ['employees', filters],
    queryFn: () => tenantApi.getPaginated<Employee>('/employees', { params: filters }),
    placeholderData: (previousData) => previousData,
  });
}

export function useCreateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employee: Partial<Employee> & { password?: string }) =>
      tenantApi.postOne<Employee>('/employees', employee),
    onSuccess: async () => {
      await refreshEmployeeQueries(queryClient);
      toast.success('Employee created successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to create employee'));
    },
  });
}

export function useUpdateEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...data }: Partial<Employee> & { id: string; password?: string }) =>
      tenantApi.putOne<Employee>(`/employees/${id}`, data),
    onSuccess: async (_data, variables) => {
      await refreshEmployeeQueries(queryClient, variables.id);
      toast.success('Employee updated successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to update employee'));
    },
  });
}

export function useDeleteEmployee() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => tenantApi.deleteOne(`/employees/${id}`),
    onSuccess: async (_data, id) => {
      await refreshEmployeeQueries(queryClient, id);
      toast.success('Employee deleted successfully');
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, 'Failed to delete employee'));
    },
  });
}
